import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  MenuItem,
  InputAdornment,
  IconButton,
  FormHelperText,
  Chip,
  Dialog,
  Grid,
  useTheme,
  Paper,
  Button,
  Divider,
} from "@mui/material";
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import useMediaQuery from "@mui/material/useMediaQuery";
import "./App.css";
import Loader from "./Loader";
import axios from "./axiosConfig";
import AdjustIcon from "@mui/icons-material/Adjust";
import CheckIcon from '@mui/icons-material/Check';

import images from "./logo2.png";
import images3 from "./grocery-cart.png";
import images2 from "./ticket.png";
import images1 from "./transfer2.png";

const ShipmentTrackingMobile = () => {
  const { db, trackno } = useParams();
  const tokent =
    "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldmVudGxpc3QiOlt7IlVzZXJJRCI6IjEiLCJMb2dpbkNvZGUiOiIwMSIsIkxvZ2luTmFtZSI6IkFkbWluIiwiRW1haWxJZCI6ImFkbWluQGdtYWlsLmNvbSIsIlVzZXJUeXBlIjoiQURNSU4ifV0sImlhdCI6MTYzODM1NDczMV0.ZW6zEHIXTxfT-QWEzS6-GuY7bRupf2Jc_tp4fXIRabQ";
  const [country, setCountry] = useState(db || "IN");
  const [trackingNumber, setTrackingNumber] = useState(trackno || "");
  const [errorMessage, setErrorMessage] = useState("");
  const [openLoader, setOpenLoader] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [headerData, setHeaderData] = useState(null);
  const [bookingData, setBookingData] = useState([]);
  const [trackingMaster, setTrackingMaster] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  // Animation states - simplified for mobile
  const [activeStep, setActiveStep] = useState(-1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const cardRef = useRef(null);
  
  const theme = useTheme();
  const belowMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    GetDBCountries();
  }, []);

  useEffect(() => {
    if (db && trackno) {
      handleTrack();
    }
  }, [db, trackno]);

  // Simplified animation effect for mobile
  useEffect(() => {
    if (completedSteps.length > 0 && trackingMaster.length > 0 && !isAnimating) {
      startSimpleAnimation();
    }
  }, [completedSteps, trackingMaster]);

  const startSimpleAnimation = () => {
    setIsAnimating(true);
    setActiveStep(-1);
    
    // Find "PARCEL BOOKED" index
    const parcelBookedIndex = trackingMaster.findIndex(
      step => step.TrackName === "PARCEL BOOKED"
    );
    
    let currentStep = parcelBookedIndex !== -1 ? parcelBookedIndex : 0;
    
    const animateStep = () => {
      if (currentStep < trackingMaster.length) {
        const step = trackingMaster[currentStep];
        const isStepCompleted = completedSteps.some(s => (s.TrackName).toLowerCase() === (step.TrackName).toLowerCase());
        
        if (isStepCompleted) {
          setActiveStep(currentStep);
          
          // Use setTimeout instead of requestAnimationFrame for better mobile compatibility
          setTimeout(() => {
            currentStep++;
            animateStep();
          }, 500); // Reduced delay for mobile
        } else {
          // Skip non-completed steps faster
          currentStep++;
          setTimeout(animateStep, 100);
        }
      } else {
        setIsAnimating(false);
        setAnimationComplete(true);
      }
    };
    
    // Start animation after a short delay
    setTimeout(animateStep, 300);
  };

  // Get all Active Buyer data
  const GetDBCountries = async () => {
    try {
      const res = await axios.instance.get("/IN/GetDBCountries", {
        headers: { Authorization: tokent, "Content-Type": "application/json" },
      });
      setCountryData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchTrackerMaster = async () => {
    try {
      const url = `/${country}/GetTrackingMaster`;
      const response = await axios.instance.post(
        url,
        { TrackNo: trackingNumber },
        {
          headers: { 
            Authorization: tokent, 
            "Content-Type": "application/json" 
          },
        }
      );
      
      const masterData = response.data;
      setTrackingMaster(masterData);
      return masterData;
    } catch (error) {
      console.error("Error fetching tracker master:", error);
      throw error;
    }
  };

  // Fetch tracking details based on selected country and tracking number
  const fetchTrackingDetails = async () => {
    try {
      const url = `/${country}/GetTrackingDetails`;
      const response = await axios.instance.post(
        url,
        { TrackNo: trackingNumber },
        {
          headers: {
            Authorization: tokent,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data.resultSet1[0].valid === "Invalid Tracking Number") {
        setErrorMessage("Invalid Tracking Number");
        // Clear header data and booking data when invalid
        setHeaderData(null);
        setBookingData([]);
        setTrackingMaster([]);
        setCompletedSteps([]);
        return null;
      }
      
      const { resultSet1, resultSet2 } = response.data;
      setHeaderData(resultSet1[0]);
      setBookingData(resultSet2);
      return resultSet2;
    } catch (error) {
      console.error("Error fetching tracking details:", error);
      setErrorMessage("Error fetching tracking details. Please try again.");
      // Clear data on error
      setHeaderData(null);
      setBookingData([]);
      setTrackingMaster([]);
      setCompletedSteps([]);
      throw error;
    }
  };

  // Function to determine completed steps
  const findCompletedSteps = (actualTrackingData, master = trackingMaster) => {
    if (!master.length || !actualTrackingData || !actualTrackingData.length) return [];
    
    // Extract completed track names from actual data
    const completedTrackNames = actualTrackingData.map(item => item.TrackName);
    
    // Sort completed steps according to master order
    const sortedCompleted = master.filter(masterStep => 
      completedTrackNames.some(name => name.toLowerCase() === masterStep.TrackName.toLowerCase())
    );
    
    setCompletedSteps(sortedCompleted);
    return sortedCompleted;
  };

  // Simplified helper functions for mobile
  const isStepActive = (index) => {
    return isAnimating && activeStep >= index;
  };

  const isStepCompleted = (trackName) => {
    // When animation is complete, show all completed steps
    if (animationComplete || !isAnimating) {
      return completedSteps.some(step => (step.TrackName).toLowerCase() === (trackName).toLowerCase());
    }
    
    // During animation, only show completed if it's been animated
    return completedSteps.some(step => (step.TrackName).toLowerCase() === (trackName).toLowerCase()) && 
           activeStep >= trackingMaster.findIndex(step => (step.TrackName).toLowerCase() === (trackName).toLowerCase());
  };

  const handleTrack = async () => {
    if (!trackingNumber) {
      setErrorMessage("Tracking number cannot be empty.");
      return;
    }
    
    setErrorMessage("");
    setOpenLoader(true);
    setActiveStep(-1);
    setIsAnimating(false);
    setAnimationComplete(false);

    try {
      const masterData = await fetchTrackerMaster();
      const actualData = await fetchTrackingDetails();
      if (masterData && actualData) {
        findCompletedSteps(actualData, masterData);
      }
    } catch (err) {
      console.error("Error during tracking fetch:", err);
    } finally {
      setOpenLoader(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleTrack();
    }
  };

  // Optimized animation styles for mobile
  const animationStyles = `
    /* Minimal animations for mobile performance */
    @keyframes simpleFadeIn {
      from { opacity: 0.5; }
      to { opacity: 1; }
    }
    
    @keyframes simplePulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    
    @keyframes simpleGlow {
      0%, 100% { box-shadow: 0 0 3px rgba(76, 175, 80, 0.5); }
      50% { box-shadow: 0 0 8px rgba(76, 175, 80, 0.8); }
    }
    
    /* Use transforms and opacity only - avoid heavy properties */
    .mobile-step-active {
      animation: simpleFadeIn 0.4s ease-out forwards;
      transform: translateZ(0); /* Trigger GPU acceleration */
      will-change: transform, opacity;
    }
    
    .mobile-dot-pulse {
      animation: simplePulse 0.8s ease-in-out;
      transform: translateZ(0);
    }
    
    .mobile-card-glow {
      animation: simpleGlow 1.5s ease-in-out;
    }
    
    /* Optimize transitions - use only necessary properties */
    .optimized-transition {
      transition: opacity 0.3s ease, transform 0.3s ease;
      transform: translateZ(0);
    }
    
    /* Disable animations for users who prefer reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .mobile-step-active,
      .mobile-dot-pulse,
      .mobile-card-glow,
      .optimized-transition {
        animation: none !important;
        transition: none !important;
      }
    }
  `;

  return (
    <Box
      sx={{
        padding: 4,
        backgroundColor: "white",
        borderRadius: belowMediumScreen ? 0 : 10,
      }}
      my={belowMediumScreen ? 0 : 12}
    >
      <style>{animationStyles}</style>
      
      {/* Header Section */}
      <Box sx={{ textAlign: "center" }} mb={4}>
        <a
          href="https://www.teeparam.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <div>
            <img src={images} 
                     style={{
                  objectFit: 'contain',
                  maxWidth: '50%',
                }}
            alt="logo" />
          </div>
        </a>
        <Typography
          variant={belowMediumScreen ? "subtitle2" : "h5"}
          sx={{
            marginTop: 2,
            marginBottom: 2,
            fontWeight: "bold",
            color: "#536493",
          }}
        >
          Track Your Shipment Status
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: 2,
            borderRadius: 30,
          }}
        >
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="enter tracking number"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TextField
                    select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    variant="outlined"
                    sx={{
                      backgroundColor: "#fff",
                      border: "none",
                      "& .MuiSelect-select": {
                        padding: "8px 12px",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                    SelectProps={{
                      renderValue: (selected) => {
                        const selectedCountry = countryData.find(
                          (option) => option.CountryCode === selected
                        );
                        return selectedCountry
                          ? selectedCountry.CountryCode
                          : selected;
                      },
                    }}
                  >
                    {countryData.map((option) => (
                      <MenuItem
                        key={option.CountryCode}
                        value={option.CountryCode}
                      >
                        {option.CountryName}
                      </MenuItem>
                    ))}
                  </TextField>
                </InputAdornment>
              ),
            }}
            sx={{
              width: belowMediumScreen ? "80vw" : "50vw",
              backgroundColor: "#fff",
              border: "none",
            }}
            error={Boolean(errorMessage)}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTrack}
          size="small"
          endIcon={<ArrowRightIcon />}
          sx={{ 
            borderRadius: 10, 
            mt: 2,
            transition: 'transform 0.2s ease',
            '&:active': {
              transform: 'scale(0.98)',
            }
          }}
        >
          Track
        </Button>
        
        {errorMessage && (
          <FormHelperText error sx={{ textAlign: "center", marginTop: 1 }}>
            {errorMessage}
          </FormHelperText>
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />
      
      {/* Only show tracking card when we have valid header data */}
      {headerData && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            opacity: headerData ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
          ref={cardRef}
        >
          <Card
            sx={{ 
              boxShadow: 0, 
              width: belowMediumScreen ? "100vw" : "70vw",
            }}
            elevation={0}
            py={2}
            fullWidth
          >
            <CardContent>
              <Divider fullWidth sx={{ mt: 1 }}>
                <Chip label="Tracking Status" size="small" color="primary" />
              </Divider>

              <Stack
                direction={belowMediumScreen ? "column" : "row"}
                divider={
                  <Divider
                    orientation={belowMediumScreen ? "horizontal" : "vertical"}
                    flexItem
                  />
                }
                mt={3}
                mb={0}
                justifyContent={"space-evenly"}
                alignItems={"center"}
                component={Paper}
                elevation={2}
                p={2}
                borderRadius={7}
                className="optimized-transition"
              >
                <Stack
                  direction="column"
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h6" color="#50B498" align="center">
                    Origin
                  </Typography>
                  <Typography variant="subtitle2" align="center">
                    {headerData.SCountry || "N/A"}
                  </Typography>
                </Stack>

                <Stack
                  direction="column"
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h6" color="#50B498" align="center">
                    Destination
                  </Typography>
                  <Typography variant="subtitle2" align="center">
                    {headerData.RCountry || "N/A"}
                  </Typography>
                </Stack>

                {/* <Stack
                  direction="column"
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h6" color="#50B498" align="center">
                    Bill Weight
                  </Typography>
                  <Typography variant="subtitle2" align="center">
                    {headerData.TotalWeight || "0"} kg
                  </Typography>
                </Stack> */}
                <Stack
                  direction="column"
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h6" color="#50B498" align="center">
                    Actual Weight
                  </Typography>
                  <Typography variant="subtitle2" align="center">
                    {headerData.ACWeight || "0"} kg
                  </Typography>
                </Stack>

                <Stack
                  direction="column"
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h6" color="#50B498" align="center">
                    Dimensions / Box Type
                  </Typography>
                  <Typography variant="subtitle2" align="center">
                    {headerData.BoxDimensions}
                  </Typography>
                </Stack>
                     
              </Stack>

              <Box sx={{ mt: 3 }}>
                <Timeline
                  sx={{
                    [`& .${timelineItemClasses.root}:before`]: {
                      flex: 0,
                      padding: 0,
                    },
                    marginLeft: 0,
                  }}
                >
                  {trackingMaster.length > 0 ? (
                    trackingMaster.map((masterStep, index) => {
                      const isCompleted = isStepCompleted(masterStep.TrackName);
                      const isActive = isStepActive(index);
                      const actualData = bookingData.filter(
                        item => (item.TrackName).toLowerCase() === (masterStep.TrackName).toLowerCase()
                      );
                      
                      return (
                        <TimelineItem key={index}>
                          <TimelineSeparator>
                            <TimelineDot
                              variant={isCompleted ? "filled" : "outlined"}
                              sx={{
                                bgcolor: isCompleted ? 'success.light' : 'transparent',
                                color: isCompleted ? '#fff' : 'grey.500',
                                borderColor: isCompleted ? 'success.main' : 'grey.400',
                                borderWidth: isCompleted ? 2 : 1,
                                transform: 'translateZ(0)',
                                transition: 'background-color 0.3s ease, border-color 0.3s ease',
                                ...(isActive && {
                                  animation: 'simplePulse 0.8s ease-in-out',
                                })
                              }}
                            >
                              {isCompleted ? (
                                <CheckIcon sx={{
                                  fontSize: 14,
                                  transition: 'opacity 0.3s ease',
                                }} />
                              ) : (
                                <AdjustIcon sx={{
                                  fontSize: 14,
                                  opacity: 0.6,
                                }} />
                              )}
                            </TimelineDot>
                            {index < trackingMaster.length - 1 && (
                              <TimelineConnector 
                                sx={{ 
                                  bgcolor: isCompleted ? "success.light" : "grey.200",
                                  transition: 'background-color 0.3s ease',
                                }} 
                              />
                            )}
                          </TimelineSeparator>
                          <TimelineContent>
                            <Card
                              sx={{
                                boxShadow: 0,
                                padding: 2,
                                elevation: 0,
                                mb: 3,
                                border: isCompleted ? "1px solid" : "none",
                                borderColor: isCompleted ? "success.light" : "transparent",
                                bgcolor: isCompleted ? "rgba(76, 175, 80, 0.03)" : "transparent",
                                transform: 'translateZ(0)',
                                transition: 'all 0.3s ease',
                                opacity: isCompleted ? 1 : 0.7,
                                ...(isActive && {
                                  animation: 'simpleFadeIn 0.4s ease-out',
                                })
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color={isCompleted ? "success.dark" : "primary"}
                                textTransform={"capitalize"}
                                fontWeight="bold"
                                sx={{
                                  transition: 'color 0.3s ease',
                                  ...(isActive && {
                                    animation: 'simpleFadeIn 0.4s ease-out',
                                  })
                                }}
                              >
                                {masterStep.TrackName}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                color="#16325B"
                                fontWeight={"bold"}
                                sx={{
                                  opacity: isCompleted ? 0.9 : 0.6,
                                  transition: 'opacity 0.3s ease',
                                }}
                              >
                                No. of Packages: {headerData.NoofBox || "N/A"}
                              </Typography>
                              
                              {actualData.length > 0 ? (
                                actualData.map((packageData, pkgIdx) => (
                                  <div key={pkgIdx} style={{ marginBottom: 8, marginTop: 8 }}>
                                    <Typography
                                      variant="caption"
                                      color={isCompleted ? "success.main" : "text.secondary"}
                                      sx={{
                                        transition: 'color 0.3s ease',
                                        display: 'block',
                                        marginBottom: 0.5,
                                      }}
                                    >
                                      <span
                                        style={{
                                          color: isCompleted ? "#508D4E" : "#9e9e9e",
                                          fontWeight: "bold",
                                          transition: 'color 0.3s ease',
                                        }}
                                      >
                                        Pkg {packageData.BoxNo}
                                      </span>
                                      : {packageData.BookTrackDate}{" "}
                                      {packageData.BookTrackTime}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      textTransform={"lowercase"}
                                      color={isCompleted ? "success.dark" : "text.primary"}
                                      sx={{
                                        opacity: isCompleted ? 0.8 : 0.6,
                                        transition: 'opacity 0.3s ease',
                                        fontSize: '0.85rem',                                       
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",

                                      }}
                                    >
                                      {packageData.Remarks || " "}
                                    </Typography>
                                  </div>
                                ))
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  fontStyle="italic"
                                  sx={{
                                    opacity: isCompleted ? 0.8 : 0.4,
                                    transition: 'opacity 0.3s ease',
                                    mt: 1,
                                  }}
                                >
                                  {isCompleted ? "Completed" : "Pending"}
                                </Typography>
                              )}
                            </Card>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    })
                  ) : (
                    bookingData
                      .reduce((acc, curr) => {
                        const existingTrack = acc.find(
                          (item) => (item.TrackName).toLowerCase() === (curr.TrackName).toLowerCase()
                        );
                        if (!existingTrack) {
                          acc.push({
                            TrackName: curr.TrackName,
                            Remarks: curr.Remarks,
                            packages: [],
                          });
                        }

                        const currentIndex = acc.findIndex(
                          (item) => (item.TrackName).toLowerCase() === (curr.TrackName).toLowerCase()
                        );
                        acc[currentIndex].packages.push({
                          BoxNo: curr.BoxNo,
                          BookTrackDate: curr.BookTrackDate,
                          BookTrackTime: curr.BookTrackTime,
                          Remarks: curr.Remarks,
                        });

                        return acc;
                      }, [])
                      .reverse()
                      .map((event, index) => (
                        <TimelineItem key={index}>
                          <TimelineSeparator>
                            <TimelineDot color="primary">
                              <AdjustIcon />
                            </TimelineDot>
                            {index < bookingData.length - 1 && (
                              <TimelineConnector />
                            )}
                          </TimelineSeparator>
                          <TimelineContent>
                            <Card
                              sx={{
                                boxShadow: 0,
                                padding: 1,
                                elevation: 0,
                                mb: 4,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                color="primary"
                                textTransform={"capitalize"}
                                fontWeight="bold"
                              >
                                {event.TrackName}
                              </Typography>
                              <Typography
                                variant="subtitle2"
                                color="#16325B"
                                fontWeight={"bold"}
                              >
                                No. of Packages: {headerData.NoofBox}
                              </Typography>
                              {event.packages
                                .reverse()
                                .map((packageData, pkgIdx) => (
                                  <div key={pkgIdx} style={{ marginBottom: 5 }}>
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      <span
                                        style={{
                                          color: "#508D4E",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        Pkg {packageData.BoxNo}
                                      </span>
                                      : {packageData.BookTrackDate}{" "}
                                      {packageData.BookTrackTime}
                                      <br />
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      textTransform={"lowercase"}
                                      sx={{
                                          whiteSpace: "normal",
                                          wordBreak: "break-word",
                                          overflowWrap: "anywhere",
                                        }}
                                    >
                                      {packageData.Remarks || " "}
                                    </Typography>
                                  </div>
                                ))}
                            </Card>
                          </TimelineContent>
                        </TimelineItem>
                      ))
                  )}
                </Timeline>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
      
      {/* Service Cards Section */}
      <Divider fullWidth sx={{ mt: 1, mx: 10, mb: 3 }}>
        <Chip
          label="Our Services"
          size="small"
          color="info"
          variant="outlined"
        />
      </Divider>
      <Grid container spacing={3} sx={{ mb: 2 }}>
        {[
          {
            name: "Money Transfer",
            imgSrc: images1,
            link: "https://teeparamexchange.co.uk/",
          },
          {
            name: "Air Ticketing",
            imgSrc: images2,
            link: "https://www.whitelabelhosting.co.uk/",
          },
          // {
          //   name: "FMCG Wholesaler",
          //   imgSrc: images3,
          //   link: "https://www.teeparamwholesale.co.uk/",
          // },
        ].map((service) => (
          <Grid
            item
            xs={12}
            key={service.name}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <a
              href={service.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Card
                sx={{
                  boxShadow: 2,
                  textAlign: "center",
                  borderRadius: 7,
                  padding: 1,
                  mx: 2,
                  transition: "transform 0.3s ease",
                  "&:active": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      width: belowMediumScreen ? 20 : 50,
                      height: belowMediumScreen ? 20 : 50,
                      backgroundColor: "#2F3645",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "0 auto",
                      marginBottom: 1,
                      padding: 2,
                    }}
                  >
                    <img
                      src={service.imgSrc}
                      alt={service.name}
                      style={{
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </Box>
                  <Typography
                    variant={belowMediumScreen ? "body2" : "subtitle2"}
                    color="#2F3645"
                    sx={{ fontWeight: "bold" }}
                  >
                    {service.name}
                  </Typography>
                </CardContent>
              </Card>
            </a>
          </Grid>
        ))}
      </Grid>

      {/* Loader popup dialog box */}
      <Dialog
        open={openLoader}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
        }}
      >
        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            backgroundColor: "#ffffff00",
          }}
        >
          <Loader />
        </Stack>
      </Dialog>
    </Box>
  );
};

export default ShipmentTrackingMobile;